// SignInForm.tsx
import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SignInFormData, signInSchema } from "./validationSchema";
import { EmailTextField, PasswordTextField } from "./TextField";
import { useAuth } from "../../context/AuthContext";

interface SignInFormProps {
    onForgotPassword: () => void;
}

export default function SignInForm({ onForgotPassword }: SignInFormProps) {
    const [showPassword, setShowPassword] = useState(false);
    const { signIn, isLoading } = useAuth();

    const methods = useForm<SignInFormData>({
        resolver: zodResolver(signInSchema),
        mode: "onChange"
    });
    const { formState } = methods;

    const onSubmit = async (data: SignInFormData) => {
        try {
            await signIn(data.email, data.password);
        } catch (error) {
            // Error handling is now managed in AuthContext
            console.error("Form submission error:", error);
        }
    };

    return (
        <FormProvider {...methods}>
            <Box
                component="form"
                onSubmit={methods.handleSubmit(onSubmit)}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2
                }}
            >
                <Typography
                    variant="h4"
                    textAlign="center"
                    gutterBottom
                    color="primary"
                    sx={{
                        fontWeight: 500,
                        mb: 3
                    }}
                >
                    Sign In
                </Typography>

                <EmailTextField
                    label="Email"
                    placeholder="Please enter your email"
                    fullWidth
                    sx={{
                        "& .MuiInputBase-root": {
                            bgcolor: "background.paper"
                        }
                    }}
                />

                <PasswordTextField
                    label="Password"
                    placeholder="Please enter your password"
                    fullWidth
                    showPassword={showPassword}
                    onTogglePasswordVisibility={() => setShowPassword(!showPassword)}
                    sx={{
                        "& .MuiInputBase-root": {
                            bgcolor: "background.paper"
                        }
                    }}
                />

                <Button variant="contained" type="submit" fullWidth disabled={!formState.isValid || isLoading}>
                    Login
                </Button>

                <Button
                    variant="text"
                    type="button"
                    fullWidth
                    onClick={onForgotPassword}
                    sx={{
                        mt: 1,
                        color: "text.secondary"
                    }}
                >
                    Forgot Password?
                </Button>
            </Box>
        </FormProvider>
    );
}
